import { useContext, useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import moment from "moment-timezone";
import dayjs from "dayjs";
import AuthContext from "../../lib/auth-context";
import ThemeContext from "../../lib/theme-context";
import { DataFetcher } from "../../utils/dataFetcher";

import throwMessage from "../../utils/throwMessage";
import { getJSON } from "../../utils/safeLocalStorage";

import CustomSelect from "../../components/select/index";
import ConfirmDialog from "../../components/modals/confirm-dialog";
import InstrumentSelect from "../../components/select/InstrumentSelect";
import CustomMultiTextField from "../../components/textfields/multiRow_standard";
import VoiceToText from "../../components/VoiceToText";
import CustomDatePicker from "../../components/dateTimePickers/datePicker";

import Tooltip from "@mui/material/Tooltip";

import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import ShareIcon from "@mui/icons-material/Share";

import EnhancedImageLightbox from "../../components/EnhancedImageLightbox";

import useWindowDimensions from "../../utils/width";

const imgStyle = {
  width: "100%",
  height: "430px",
  objectFit: "cover",
  borderRadius: "16px",
  cursor: "pointer",

  transition: "transform 0.3s ease, box-shadow 0.3s ease",
};

const SingleForecasts = ({
  setSingleForecastView,
  handleScroll,
  setSingleReflectionView,
}) => {
  const { width } = useWindowDimensions();
  const auth = useContext(AuthContext);
  const theme = useContext(ThemeContext);

  const [tradeId, setTradeId] = useState("");
  const [liveTradeIds, setLiveTradeIds] = useState([]);
  const [backtestTradeIds, setBacktestTradeIds] = useState([]);
  const [backtestTitle, setBacktestTitle] = useState("");
  const [forecast_id, setForecast_id] = useState("");
  const [pair, setPair] = useState("");
  const [direction, setDirection] = useState("");
  const [date, setDate] = useState(dayjs(new Date()));
  const [sequence, setSequence] = useState(undefined);
  const [positioning, setPositioning] = useState(undefined);

  const [grade, setGrade] = useState(undefined);
  const [forecastType, setForecastType] = useState(undefined);
  const [dailyLink, setDailyLink] = useState("");
  const [hourlyLink, setHourlyLink] = useState("");
  const [dailyDescrtpiton, setDailyDescrtpiton] = useState("");
  const [hourlyDescrtpiton, setHourlyDescrtpiton] = useState("");

  const [lowerTimeframeName, setLowerTimeframeName] = useState("");
  const [higherTimeframeName, setHigherTimeframeName] = useState("");

  const [changeDate, setChangeDate] = useState(false);

  const [sections, setSections] = useState([]);
  const [showAfterSections, setShowAfterSections] = useState([]);

  const updateSection = (index, field, value) => {
    setSections((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const toggleAfterSection = (index) => {
    setShowAfterSections((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const [formSubmitted, setFormSubmitted] = useState(false);

  const [isHoveringNext, setIsHoveringNext] = useState(false);
  const [isHoveringBack, setIsHoveringBack] = useState(false);
  const [isHoveringShare, setIsHoveringShare] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [showTradeInfo, setShowTradeInfo] = useState(false);
  const [showDeleteIcon, setShowDeleteIcon] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showScreenshotDeleteConfirm, setShowScreenshotDeleteConfirm] = useState(false);
  const [screenshotToDelete, setScreenshotToDelete] = useState(null);

  const handleImageMouseEnter = (index) => {
    setShowDeleteIcon(index);
  };

  const handleImageMouseLeave = (index) => {
    setShowDeleteIcon(null);
  };

  const handleDeleteScreenshot = (index, field) => {
    setScreenshotToDelete({ index, field });
    setShowScreenshotDeleteConfirm(true);
  };

  const confirmDeleteScreenshot = () => {
    if (screenshotToDelete) {
      updateSection(screenshotToDelete.index, screenshotToDelete.field, "");
      setScreenshotToDelete(null);
    }
  };

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Calculate dynamic rows based on content
  const calculateRows = (text) => {
    if (!text || text.trim() === "") return 3;
    const lineCount = text.split("\n").length;
    const charCount = text.length;
    // Estimate rows based on characters (roughly 80 chars per row)
    const estimatedRows = Math.ceil(charCount / 80);
    return Math.max(3, Math.max(lineCount, estimatedRows));
  };

  // Helper function to check if pair is USD-related
  const isUSDRelatedPair = (pairName) => {
    if (!pairName) return false;
    const usdPairs = ['AUDUSD', 'GBPUSD', 'EURUSD', 'NZDUSD', 'USDCHF', 'USDJPY', 'USDCAD', 'XAUUSD', 'XAGUSD', 'BTCUSD', 'SPX500'];
    return usdPairs.includes(pairName);
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);
  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  useEffect(() => {
    let singleForecast;
    singleForecast = auth.forecasts.find((forecast) =>
      forecast?._id.includes(localStorage.getItem("forecast-to-view")),
    );
    if (!singleForecast) {
      singleForecast = auth.forecastsBacktesting.find((forecast) =>
        forecast?._id.includes(localStorage.getItem("forecast-to-view")),
      );
    }

    const linked_trades = auth.trades.filter(
      (t) => t.foreCastId === singleForecast?._id,
    );
    const live_trades = linked_trades.filter(t => t.id && t.id.includes("live-"));
    const backtest_trades = linked_trades.filter(t => t.testId);
    const trade_id = linked_trades.length > 0 ? linked_trades[0]._id : null;
    setForecast_id(localStorage.getItem("forecast-to-view"));
    setTradeId(trade_id);
    setLiveTradeIds(live_trades.map(t => t._id));
    setBacktestTradeIds(backtest_trades.map(t => t._id));
    
    // Get backtest title if there are backtest trades
    if (backtest_trades.length > 0) {
      const testId = backtest_trades[0].testId;
      const backtest = auth.backtests?.find(b => b.testId === testId);
      setBacktestTitle(backtest?.testId || "");
    }
    setDate(singleForecast?.date);
    setPair(singleForecast?.pair);
    setDirection(singleForecast?.direction);
    setSequence(singleForecast?.sequence);
    setPositioning(singleForecast?.positioning);
    setGrade(singleForecast?.grade);
    setForecastType(singleForecast?.forecastType);
    setDailyLink(singleForecast?.dailyLink);
    setHourlyLink(singleForecast?.hourlyLink);
    setDailyDescrtpiton(singleForecast?.dailyDescrtpiton);
    setHourlyDescrtpiton(singleForecast?.hourlyDescrtpiton);
    setLowerTimeframeName(singleForecast?.lowerTimeframeName);
    setHigherTimeframeName(singleForecast?.higherTimeframeName);

    if (singleForecast?.sections && singleForecast.sections.length > 0) {
      const mappedSections = singleForecast.sections.map((s) => ({
        timeframeName: s.timeframeName || "",
        timeframeEdit: false,
        screenshotLink: s.screenshotLink || "",
        dxyScreenshotLink: s.dxyScreenshotLink || "",
        description: s.description || "",
        afterScreenshotLink: s.afterScreenshotLink || "",
        dxyAfterScreenshotLink: s.dxyAfterScreenshotLink || "",
        afterDescription: s.afterDescription || "",
      }));
      setSections(mappedSections);
      setShowAfterSections(mappedSections.map(s => !!(s.afterScreenshotLink || s.afterDescription)));
    } else {
      const defaultSections = [
        {
          timeframeName: singleForecast?.higherTimeframeName || "",
          timeframeEdit: false,
          screenshotLink: singleForecast?.dailyLink || "",
          description: singleForecast?.dailyDescrtpiton || "",
          afterScreenshotLink: singleForecast?.dailyLinkAfter || "",
          afterDescription: singleForecast?.dailyDescrtpitonAfter || "",
        },
        {
          timeframeName: singleForecast?.lowerTimeframeName || "",
          timeframeEdit: false,
          screenshotLink: singleForecast?.hourlyLink || "",
          description: singleForecast?.hourlyDescrtpiton || "",
          afterScreenshotLink: singleForecast?.hourlyLinkAfter || "",
          afterDescription: singleForecast?.hourlyDescrtpitonAfter || "",
        },
      ];
      setSections(defaultSections);
      setShowAfterSections(defaultSections.map(s => !!(s.afterScreenshotLink || s.afterDescription)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const changeForecast = (direction, pass) => {
    let next;
    let back;

    let array = pass;

    const reflectionToView = localStorage.getItem("reflection-to-view");
    const fromTracker = localStorage.getItem("from-tracker");
    const yearFilter = localStorage.getItem("year");
    const selectedAccountId = localStorage.getItem("selected-account-id");
    const testId = localStorage.getItem("backtest-active");

    if (pass.length > 1) {
      // If opened from reflection, filter to reflection's forecasts
      if (reflectionToView) {
        // In backtesting mode, use both forecast arrays since reflections can span live and backtest
        if (auth.backtestingMode) {
          array = [...(auth.forecastsBacktesting || []), ...(auth.forecasts || [])];
        }
        const reflectionForecastIds = getJSON("reflection-forecast-ids", []);
        if (reflectionForecastIds.length > 0) {
          array = array.filter((forecast) => reflectionForecastIds.includes(forecast._id));
        }
      } else if (fromTracker === "true") {
        // Opened from results tracker
        if (auth.backtestingMode) {
          // In backtesting mode, use both forecast arrays since forecasts can come from both
          // If testId is null, get it from the current forecast's associated trade
          let effectiveTestId = testId;
          if (!effectiveTestId && forecast_id) {
            const currentTrade = auth.trades.find(t => t.foreCastId === forecast_id);
            if (currentTrade && currentTrade.testId) {
              effectiveTestId = currentTrade.testId;
            }
          }
          
          // Apply same filters as results tracker to get visible trades (no year filter in backtesting)
          let trades = auth.trades;
          
          // Filter by testId if set
          if (effectiveTestId) {
            trades = trades.filter(t => t.testId === effectiveTestId);
          }
          
          // No account filter in backtesting mode - only care about testId
          
          // Get forecast IDs from filtered trades
          const backtestForecastIds = trades
            .map(t => t.foreCastId)
            .filter(id => id);
          
          array = [...(auth.forecastsBacktesting || []), ...(auth.forecasts || [])]
            .filter(f => backtestForecastIds.includes(f._id));
          
          // Always include the current forecast in the array for navigation
          if (forecast_id && !array.some(f => f._id === forecast_id)) {
            const currentForecast = [...(auth.forecastsBacktesting || []), ...(auth.forecasts || [])]
              .find(f => f._id === forecast_id);
            if (currentForecast) {
              array.push(currentForecast);
            }
          }
        } else {
          // Live mode - use the passed array (auth.forecasts)
          // Live mode - filter by linked trades
          let trades = auth.trades;
          trades = trades.filter((t) => t?.id?.includes("live-"));

          // Filter by year if set (same logic as journal)
          if (!auth.backtestingMode && yearFilter && yearFilter !== "0") {
            const currentYearSet = moment(yearFilter).format("YY");
            trades = trades.filter((t) => {
              if (!t.entryDate) return false;
              let dateArray = t.entryDate.split("/");
              let tradeYear = dateArray[2];
              return Number(tradeYear) === Number(currentYearSet);
            });
          }

          // Filter by account ID (same logic as journal)
          if (selectedAccountId && selectedAccountId !== "All") {
            if (selectedAccountId === "All Personal") {
              trades = trades.filter(
                (trade) => trade.equityType === "Personal Capital",
              );
            } else if (selectedAccountId === "All Investor") {
              trades = trades.filter(
                (trade) => trade.equityType === "Investor Capital",
              );
            } else {
              // Filter by specific account ID
              const user = getJSON("user", {});
              const capitalAccounts = user.capitalAccounts || [];
              const selectedAccount = capitalAccounts.find(
                (acc) => acc.id === selectedAccountId,
              );

              if (selectedAccount) {
                trades = trades.filter((trade) => {
                  // If trade has accountId field, use it
                  if (trade.accountId) {
                    return trade.accountId === selectedAccountId;
                  }
                  // Otherwise, fall back to equityType matching
                  if (selectedAccount.type === "Personal") {
                    return trade.equityType === "Personal Capital";
                  } else if (selectedAccount.type === "Investor") {
                    return trade.equityType === "Investor Capital";
                  }
                  return false;
                });
              }
            }
          }

          // Get forecast IDs from filtered trades
          const forecastIds = trades
            .map((t) => t.foreCastId)
            .filter((id) => id); // Remove undefined/null

          // Only include forecasts that are linked to visible trades
          array = array.filter((forecast) => forecastIds.includes(forecast._id));
        }
      } else {
        // Opened from calendar - filter by week only (all forecasts in that week)
        array = array.filter((forecast) => {
          return moment(date).isSame(forecast.date, "week");
        });
      }

      array = array.sort((a, b) => a.pair.localeCompare(b.pair));

      array = array.sort((a, b) => {
        return moment(a.date).diff(b.date);
      });

      const foundIndex = array.findIndex((x) => x._id === forecast_id);

      next = foundIndex + 1;
      back = foundIndex - 1;

      // Stop at the ends of the list - no wrap-around
      if (foundIndex === 0) {
        back = -1; // No older forecast
      }
      if (foundIndex + 1 === array.length) {
        next = -1; // No newer forecast
      }

      if (direction === "Next") {
        // Stop if at the end of the list
        if (next === -1 || next >= array.length) {
          return;
        }
        
        if (!array[next]) return;
        const trade_id = auth.trades.find(
          (t) => t.foreCastId === array[next]?._id,
        );
        setTradeId(trade_id?._id);
        setForecast_id(array[next]?._id);
        setDate(array[next].date);
        setPair(array[next].pair);
        setDirection(array[next].direction);
        setSequence(array[next].sequence);
        setPositioning(array[next].positioning);
        setGrade(array[next].grade);
        setForecastType(array[next].forecastType);
        setDailyLink(array[next].dailyLink);
        setHourlyLink(array[next].hourlyLink);
        setDailyDescrtpiton(array[next].dailyDescrtpiton);
        setHourlyDescrtpiton(array[next].hourlyDescrtpiton);
        setLowerTimeframeName(array[next].lowerTimeframeName);
        setHigherTimeframeName(array[next].higherTimeframeName);

        if (array[next]?.sections && array[next].sections.length > 0) {
          const mappedSections = array[next].sections.map((s) => ({
            timeframeName: s.timeframeName || "",
            timeframeEdit: false,
            screenshotLink: s.screenshotLink || "",
            dxyScreenshotLink: s.dxyScreenshotLink || "",
            description: s.description || "",
            afterScreenshotLink: s.afterScreenshotLink || "",
            dxyAfterScreenshotLink: s.dxyAfterScreenshotLink || "",
            afterDescription: s.afterDescription || "",
          }));
          setSections(mappedSections);
          setShowAfterSections(mappedSections.map(s => !!(s.afterScreenshotLink || s.afterDescription)));
        } else {
          const defaultSections = [
            {
              timeframeName: array[next].higherTimeframeName || "",
              timeframeEdit: false,
              screenshotLink: array[next].dailyLink || "",
              description: array[next].dailyDescrtpiton || "",
              afterScreenshotLink: array[next].dailyLinkAfter || "",
              afterDescription: array[next].dailyDescrtpitonAfter || "",
            },
            {
              timeframeName: array[next].lowerTimeframeName || "",
              timeframeEdit: false,
              screenshotLink: array[next].hourlyLink || "",
              description: array[next].hourlyDescrtpiton || "",
              afterScreenshotLink: array[next].hourlyLinkAfter || "",
              afterDescription: array[next].hourlyDescrtpitonAfter || "",
            },
          ];
          setSections(defaultSections);
          setShowAfterSections(defaultSections.map(s => !!(s.afterScreenshotLink || s.afterDescription)));
        }
      } else if (direction === "Back") {
        // Stop if at the start of the list
        if (back === -1 || back < 0) {
          return;
        }
        
        if (!array[back]) return;
        const trade_id = auth.trades.find(
          (t) => t.foreCastId === array[back]?._id,
        );
        setTradeId(trade_id?._id);
        setForecast_id(array[back]?._id);
        setDate(array[back].date);
        setPair(array[back].pair);
        setDirection(array[back].direction);
        setSequence(array[back].sequence);
        setPositioning(array[back].positioning);
        setGrade(array[back].grade);
        setDailyLink(array[back].dailyLink);
        setForecastType(array[back].forecastType);
        setHourlyLink(array[back].hourlyLink);
        setDailyDescrtpiton(array[back].dailyDescrtpiton);
        setHourlyDescrtpiton(array[back].hourlyDescrtpiton);
        setLowerTimeframeName(array[back].lowerTimeframeName);
        setHigherTimeframeName(array[back].higherTimeframeName);

        if (array[back]?.sections && array[back].sections.length > 0) {
          const mappedSections = array[back].sections.map((s) => ({
            timeframeName: s.timeframeName || "",
            timeframeEdit: false,
            screenshotLink: s.screenshotLink || "",
            dxyScreenshotLink: s.dxyScreenshotLink || "",
            description: s.description || "",
            afterScreenshotLink: s.afterScreenshotLink || "",
            dxyAfterScreenshotLink: s.dxyAfterScreenshotLink || "",
            afterDescription: s.afterDescription || "",
          }));
          setSections(mappedSections);
          setShowAfterSections(mappedSections.map(s => !!(s.afterScreenshotLink || s.afterDescription)));
        } else {
          const defaultSections = [
            {
              timeframeName: array[back].higherTimeframeName || "",
              timeframeEdit: false,
              screenshotLink: array[back].dailyLink || "",
              description: array[back].dailyDescrtpiton || "",
              afterScreenshotLink: array[back].dailyLinkAfter || "",
              afterDescription: array[back].dailyDescrtpitonAfter || "",
            },
            {
              timeframeName: array[back].lowerTimeframeName || "",
              timeframeEdit: false,
              screenshotLink: array[back].hourlyLink || "",
              description: array[back].hourlyDescrtpiton || "",
              afterScreenshotLink: array[back].hourlyLinkAfter || "",
              afterDescription: array[back].hourlyDescrtpitonAfter || "",
            },
          ];
          setSections(defaultSections);
          setShowAfterSections(defaultSections.map(s => !!(s.afterScreenshotLink || s.afterDescription)));
        }
      }
    }
  };
  const handleViewTrades = (mode, tradeIds) => {
    // Find a trade from the selected mode to get its accountId
    const linkedTrade = auth.trades.find((t) => tradeIds.includes(t._id));

    // Set the account selector based on the trade's accountId
    if (linkedTrade && linkedTrade.accountId) {
      localStorage.setItem("selected-account-id", linkedTrade.accountId);
    } else if (linkedTrade && linkedTrade.equityType) {
      // Fallback to equity type for backward compatibility
      const user = getJSON("user", {});
      const capitalAccounts = user.capitalAccounts || [];

      if (linkedTrade.equityType === "Personal Capital") {
        const personalAccount = capitalAccounts.find(
          (acc) => acc.type === "Personal" && acc.isActive,
        );
        if (personalAccount) {
          localStorage.setItem("selected-account-id", personalAccount.id);
        }
      } else if (linkedTrade.equityType === "Investor Capital") {
        const investorAccount = capitalAccounts.find(
          (acc) => acc.type === "Investor" && acc.isActive,
        );
        if (investorAccount) {
          localStorage.setItem("selected-account-id", investorAccount.id);
        }
      }
    }

    // Set the appropriate mode
    if (mode === "backtest") {
      localStorage.setItem("backtest-active", "true");
      auth.setBacktestingMode(true);
    } else {
      localStorage.removeItem("backtest-active");
      auth.setBacktestingMode(false);
    }

    auth.setTab(1);
    localStorage.setItem("trade-to-view", tradeIds[0]);
    localStorage.setItem("linked-trade-ids", JSON.stringify(tradeIds));
    localStorage.removeItem("forecast-to-view");
  };

  const goBack = () => {
    setSingleForecastView(false);
    // handleScroll();
    localStorage.removeItem("forecast-to-view");
    localStorage.removeItem("reflection-forecast-ids");
    if (localStorage.getItem("reflection-to-view")) {
      setSingleReflectionView(true);
    }
    if (localStorage.getItem("from-tracker") === "true") {
      // Find the trade associated with this forecast to get its accountId
      const associatedTrade = auth.trades.find(
        (t) => t.foreCastId === forecast_id,
      );

      // Set the account selector based on the trade's accountId
      if (associatedTrade && associatedTrade.accountId) {
        localStorage.setItem("selected-account-id", associatedTrade.accountId);
      } else if (associatedTrade && associatedTrade.equityType) {
        // Fallback to equity type for backward compatibility
        const user = getJSON("user", {});
        const capitalAccounts = user.capitalAccounts || [];

        if (associatedTrade.equityType === "Personal Capital") {
          const personalAccount = capitalAccounts.find(
            (acc) => acc.type === "Personal" && acc.isActive,
          );
          if (personalAccount) {
            localStorage.setItem("selected-account-id", personalAccount.id);
          }
        } else if (associatedTrade.equityType === "Investor Capital") {
          const investorAccount = capitalAccounts.find(
            (acc) => acc.type === "Investor" && acc.isActive,
          );
          if (investorAccount) {
            localStorage.setItem("selected-account-id", investorAccount.id);
          }
        }
      }

      // Restore the filter state that was saved before opening the forecast
      const savedTradeFilter = localStorage.getItem("tracker-trade-filter-before-forecast");
      const savedLinkedFilter = localStorage.getItem("tracker-linked-filter-before-forecast");

      if (savedTradeFilter) {
        localStorage.setItem("trade-to-view", savedTradeFilter);
      } else {
        localStorage.removeItem("trade-to-view");
      }

      if (savedLinkedFilter) {
        localStorage.setItem("linked-trade-ids", savedLinkedFilter);
      } else {
        localStorage.removeItem("linked-trade-ids");
      }

      // Clear the saved filter state
      localStorage.removeItem("tracker-trade-filter-before-forecast");
      localStorage.removeItem("tracker-linked-filter-before-forecast");

      auth.setTab(1);
      localStorage.setItem("screen", 1);
      localStorage.removeItem("from-tracker");
    }
  };
  const deleteForecast = async (goBack) => {
    const schema = {
      _id: localStorage.getItem("forecast-to-view"),
      backtestingMode: auth.backtestingMode,
    };

    const result = await DataFetcher("delete-forecast", schema);
    if (result) {
      if (auth.backtestingMode) {
        const filteredArray = auth.forecastsBacktesting.filter(
          (x) => x?._id !== localStorage.getItem("forecast-to-view"),
        );

        auth.setForecastsBacktesting(filteredArray);
      } else {
        const filteredArray = auth.forecasts.filter(
          (x) => x?._id !== localStorage.getItem("forecast-to-view"),
        );

        auth.setForecasts(filteredArray);
      }
      throwMessage("success", "Deleted");
      goBack();
    }
  };
  const handleSave = async () => {
    const schema = {
      _id: forecast_id,
      pair: pair,
      direction: direction,
      date: date,
      sequence: sequence,
      positioning: positioning,
      grade: grade,
      forecastType: forecastType,
      lowerTimeframeName: lowerTimeframeName,
      higherTimeframeName: higherTimeframeName,
      dailyLink: dailyLink,
      hourlyLink: hourlyLink,
      hourlyLinkAfter: sections[1]?.afterScreenshotLink || "",
      dailyLinkAfter: sections[0]?.afterScreenshotLink || "",
      dailyDescrtpiton: dailyDescrtpiton,
      hourlyDescrtpiton: hourlyDescrtpiton,
      dailyDescrtpitonAfter: sections[0]?.afterDescription || "",
      hourlyDescrtpitonAfter: sections[1]?.afterDescription || "",
      sections: sections,
      backtestingMode: auth.backtestingMode,
    };
    if (!formSubmitted);
    setFormSubmitted(true);
    const result = await DataFetcher("edit-forecast", schema);

    if (result) {
      if (auth.backtestingMode) {
        const filteredArray = auth.forecastsBacktesting.filter(
          (x) => x?._id !== forecast_id,
        );
        filteredArray.push(result);
        auth.setForecastsBacktesting(filteredArray);
      } else {
        const filteredArray = auth.forecasts.filter(
          (x) => x?._id !== forecast_id,
        );
        filteredArray.push(result);
        auth.setForecasts(filteredArray);
      }
      throwMessage("success", "Saved");
      setFormSubmitted(false);
    } else {
      throwMessage("error", "Something went wrong");
      setFormSubmitted(false);
    }
  };
  const handleEdit = (id) => {
    switch (id) {
      case "setPair":
        setPair("");
        break;
      case "setDirection":
        setDirection("");
        break;

      case "setChangeDate":
        setChangeDate(true);
        break;

      case "setSequence":
        setSequence(undefined);
        break;

      case "setPositioning":
        setPositioning(undefined);
        break;

      case "setGrade":
        setGrade(undefined);
        break;

      case "setForecastType":
        setForecastType(undefined);
        break;

      default:
        break;
    }
  };


  const handleShare = async () => {
    if (isSharing) return;

    setIsSharing(true);

    try {
      const schema = {
        _id: forecast_id,
        backtestingMode: auth.backtestingMode,
      };

      const result = await DataFetcher("generate-share-token", schema);

      if (result && result.slug) {
        const directionLower = direction ? direction.toLowerCase() : "unknown";
        const pairLower = pair ? pair.toLowerCase() : "unknown";
        // Extract just the date part from the slug (remove the pair prefix if it exists)
        const dateSlug = result.slug.includes("/")
          ? result.slug.split("/")[1]
          : result.slug;
        const shareUrl = `${window.location.origin}/forecast/${pairLower}/${directionLower}/${dateSlug}`;

        
        // Copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        throwMessage("success", "Copied to clipboard");
      } else {
        throwMessage("error", "Failed to generate share link");
      }
    } catch (error) {
      throwMessage("error", "Error sharing forecast");
    } finally {
      setIsSharing(false);
    }
  };

  const urls = [
    ...sections.map((s) => s.screenshotLink),
    ...sections.map((s) => s.afterScreenshotLink),
  ];

  const getTitle = (index) => {
    if (index < sections.length) {
      return `${sections[index].timeframeName || "Timeframe"} Before`;
    }
    const afterIndex = index - sections.length;
    if (afterIndex < sections.length) {
      return `${sections[afterIndex].timeframeName || "Timeframe"} After`;
    }
  };


  return (
    <Box
      sx={{
        overflowY: !isViewerOpen ? "scroll" : null,
        overflowX: !isViewerOpen ? "hidden" : null,
        position: !isViewerOpen ? "absolute" : "fixed",
        top: "64px",
        left: "50%",
        transform: "translateX(-50%)",
        color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
        width: "100%",
        maxWidth: "1920px",
        height: "calc(100vh - 64px)",
        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        padding: { xs: "10px 15px 30px 15px", sm: "10px 15px 15px 15px" },
        margin: "0 auto",
      }}
    >
      {isViewerOpen && (
        <EnhancedImageLightbox
          images={urls}
          labels={urls.map((_, index) => getTitle(index))}
          currentIndex={currentImage}
          onClose={closeImageViewer}
          onNavigate={(index) => setCurrentImage(index)}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: width <= 800 ? "1em 1.5em 0 1.5em" : "1em 2em 0 2em",
          gap: "1em",
        }}
      >
          <div
            onClick={() =>
              changeForecast(
                "Back",
                auth.backtestingMode
                  ? [...(auth.forecastsBacktesting || []), ...(auth.forecasts || [])]
                  : auth.forecasts,
              )
            }
            onMouseEnter={() => setIsHoveringBack(true)}
            onMouseLeave={() => setIsHoveringBack(false)}
            style={{
              cursor: "pointer",
              padding: "8px 16px",
              borderRadius: "20px",
              backgroundColor: isHoveringBack
                ? theme[0] === "dark"
                  ? "rgba(237, 158, 14, 0.15)"
                  : "rgba(237, 158, 14, 0.1)"
                : theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.1)"
                  : "rgba(107, 114, 128, 0.05)",
              border: `2px solid ${
                isHoveringBack
                  ? "#ED9E0E"
                  : theme[0] === "dark"
                    ? "#2d2e33"
                    : "#e5e7eb"
              }`,
              color: isHoveringBack
                ? "#ED9E0E"
                : theme[0] === "dark"
                  ? "#9ca3af"
                  : "#6b7280",
              fontWeight: "600",
              fontSize: "14px",
              letterSpacing: "0.5px",
              transition: "all 0.3s ease",
              transform: isHoveringBack ? "translateY(-2px)" : "translateY(0)",
            }}
          >
            ← OLDER
          </div>
          <div
            onMouseEnter={() => setIsHoveringNext(true)}
            onMouseLeave={() => setIsHoveringNext(false)}
            onClick={() =>
              changeForecast(
                "Next",
                auth.backtestingMode
                  ? [...(auth.forecastsBacktesting || []), ...(auth.forecasts || [])]
                  : auth.forecasts,
              )
            }
            style={{
              cursor: "pointer",
              padding: "8px 16px",
              borderRadius: "20px",
              backgroundColor: isHoveringNext
                ? theme[0] === "dark"
                  ? "rgba(237, 158, 14, 0.15)"
                  : "rgba(237, 158, 14, 0.1)"
                : theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.1)"
                  : "rgba(107, 114, 128, 0.05)",
              border: `2px solid ${
                isHoveringNext
                  ? "#ED9E0E"
                  : theme[0] === "dark"
                    ? "#2d2e33"
                    : "#e5e7eb"
              }`,
              color: isHoveringNext
                ? "#ED9E0E"
                : theme[0] === "dark"
                  ? "#9ca3af"
                  : "#6b7280",
              fontWeight: "600",
              fontSize: "14px",
              letterSpacing: "0.5px",
              transition: "all 0.3s ease",
              transform: isHoveringNext ? "translateY(-2px)" : "translateY(0)",
            }}
          >
            NEWER →
          </div>
        </div>

      <Grid
        container
        justifyContent="space-evenly"
        alignItems="center"
        sx={{ minHeight: "100%" }}
      >
        <Grid
          item
          xs={12}
          sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
        >
          <div
            style={{
              maxWidth: "95%",
              margin: width <= 768 ? "0.5em auto 0.5em auto" : "1em auto 0.5em auto",
              padding: width <= 768 ? "16px 20px" : "20px 32px",
              borderRadius: "16px",
              backgroundColor:
                theme[0] === "dark"
                  ? "rgba(45, 46, 51, 0.4)"
                  : "rgba(249, 250, 251, 0.8)",
              border: `1px solid ${
                theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
              }`,
              boxShadow:
                theme[0] === "dark"
                  ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            {width <= 768 ? (
              <>
                {!editMode && pair && (
                  <div style={{ textAlign: "center", marginBottom: "12px" }}>
                    <Tooltip title="Click to edit in Edit Mode">
                      <span
                        id="setPair"
                        onClick={() => editMode && handleEdit("setPair")}
                        style={{
                          fontSize: "28px",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                          cursor: "pointer",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        {pair}
                      </span>
                    </Tooltip>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Tooltip title="Click to edit in Edit Mode">
                    <span
                      id="setChangeDate"
                      onClick={() => editMode && handleEdit("setChangeDate")}
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        letterSpacing: "0.3px",
                        cursor: "pointer",
                        color:
                          theme[0] === "dark"
                            ? "rgba(252, 252, 255, 0.7)"
                            : "rgba(26, 27, 30, 0.7)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color =
                          theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e";
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color =
                          theme[0] === "dark"
                            ? "rgba(252, 252, 255, 0.7)"
                            : "rgba(26, 27, 30, 0.7)";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      {date && moment(date).format("dddd Do YYYY")}
                    </span>
                  </Tooltip>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      letterSpacing: "0.3px",
                      color:
                        theme[0] === "dark"
                          ? "rgba(252, 252, 255, 0.5)"
                          : "rgba(26, 27, 30, 0.5)",
                    }}
                  >
                    {forecastType ? `${forecastType} Forecast` : "Forecast"}
                  </span>
                </div>
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {pair && (
                  <Tooltip title="Click to edit in Edit Mode">
                    <span
                      id="setPair"
                      onClick={() => editMode && handleEdit("setPair")}
                      style={{
                        fontSize: "23px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                        color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      {pair}
                    </span>
                  </Tooltip>
                )}

                {!editMode && direction && (
                  <div
                    id="setDirection"
                    onClick={() => editMode && handleEdit("setDirection")}
                    style={{
                      display: "inline-block",
                      cursor: editMode ? "pointer" : "default",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          direction === "Short"
                            ? theme[0] === "dark"
                              ? "rgba(219, 6, 113, 0.15)"
                              : "rgba(219, 6, 113, 0.1)"
                            : direction === "Long"
                              ? theme[0] === "dark"
                                ? "rgba(38, 166, 93, 0.15)"
                                : "rgba(38, 166, 93, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)",
                        border: `2px solid ${
                          direction === "Short"
                            ? "#db0671"
                            : direction === "Long"
                              ? "#26a65d"
                              : "#ED9E0E"
                        }`,
                        color:
                          direction === "Short"
                            ? "#db0671"
                            : direction === "Long"
                              ? "#26a65d"
                              : "#ED9E0E",
                        transition: "all 0.2s ease",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                        userSelect: "none",
                        display: "inline-block",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      {direction}
                    </span>
                  </div>
                )}

                <Tooltip title="Click to edit in Edit Mode">
                  <span
                    id="setChangeDate"
                    onClick={() => editMode && handleEdit("setChangeDate")}
                    style={{
                      fontSize: "18px",
                      fontWeight: "500",
                      letterSpacing: "0.3px",
                      cursor: "pointer",
                      color:
                        theme[0] === "dark"
                          ? "rgba(252, 252, 255, 0.7)"
                          : "rgba(26, 27, 30, 0.7)",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color =
                        theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color =
                        theme[0] === "dark"
                          ? "rgba(252, 252, 255, 0.7)"
                          : "rgba(26, 27, 30, 0.7)";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    {date && moment(date).format("dddd Do MMM")}
                  </span>
                </Tooltip>

                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.3px",
                    color:
                      theme[0] === "dark"
                        ? "rgba(252, 252, 255, 0.5)"
                        : "rgba(26, 27, 30, 0.5)",
                  }}
                >
                  {forecastType ? `${forecastType} Forecast` : "Forecast"}
                </span>
              </div>
            )}

            <hr
              style={{
                width: "100%",
                border: "none",
                borderTop: `1px solid ${
                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                }`,
                margin: width <= 768 ? "12px 0 10px 0" : "20px 0 16px 0",
              }}
            />

            <div
              style={{
                display: width < 768 ? "grid" : "flex",
                gridTemplateColumns: width < 768 ? "1fr 1fr" : undefined,
                flexDirection: width < 768 ? undefined : "row",
                justifyContent: width < 768 ? "center" : "space-evenly",
                alignItems: "center",
                gap: width < 768 ? "12px" : "20px",
                flexWrap: "wrap",
              }}
            >
              {width < 768 && !editMode && direction && (
                <div
                  id="setDirection"
                  onClick={() => editMode && handleEdit("setDirection")}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    backgroundColor:
                      direction === "Short"
                        ? theme[0] === "dark"
                          ? "rgba(219, 6, 113, 0.15)"
                          : "rgba(219, 6, 113, 0.1)"
                        : direction === "Long"
                          ? theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)"
                          : theme[0] === "dark"
                            ? "rgba(237, 158, 14, 0.15)"
                            : "rgba(237, 158, 14, 0.1)",
                    border: `2px solid ${
                      direction === "Short"
                        ? "#db0671"
                        : direction === "Long"
                          ? "#26a65d"
                          : "#ED9E0E"
                    }`,
                    transition: "all 0.3s ease",
                    minWidth: width < 768 ? "100px" : "120px",
                    maxWidth: width < 768 ? "140px" : "160px",
                    textAlign: "center",
                    outline: "none",
                    WebkitTapHighlightColor: "transparent",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <p
                    style={{
                      fontSize: "8px",
                      fontWeight: "600",
                      letterSpacing: "0.8px",
                      color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                      margin: "0 0 2px 0",
                    }}
                  >
                    DIRECTION
                  </p>
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.3px",
                      margin: 0,
                      color:
                        direction === "Short"
                          ? "#db0671"
                          : direction === "Long"
                            ? "#26a65d"
                            : "#ED9E0E",
                    }}
                  >
                    {direction.toUpperCase()}
                  </p>
                </div>
              )}
              {!editMode && positioning && (
                <div
                  id="setPositioning"
                  onClick={() => editMode && handleEdit("setPositioning")}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    backgroundColor:
                      positioning === "Excellent Positioning"
                        ? theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.15)"
                          : "rgba(38, 166, 93, 0.1)"
                        : positioning === "Average Positioning"
                          ? theme[0] === "dark"
                            ? "rgba(237, 158, 14, 0.15)"
                            : "rgba(237, 158, 14, 0.1)"
                          : theme[0] === "dark"
                            ? "rgba(242, 54, 69, 0.15)"
                            : "rgba(242, 54, 69, 0.1)",
                    border: `2px solid ${
                      positioning === "Excellent Positioning"
                        ? "#26a65d"
                        : positioning === "Average Positioning"
                          ? "#ED9E0E"
                          : "#f23645"
                    }`,
                    transition: "all 0.3s ease",
                    minWidth: width < 768 ? "100px" : "120px",
                    maxWidth: width < 768 ? "140px" : "160px",
                    textAlign: "center",
                    outline: "none",
                    WebkitTapHighlightColor: "transparent",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <p
                    style={{
                      fontSize: "8px",
                      fontWeight: "600",
                      letterSpacing: "0.8px",
                      color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                      margin: "0 0 2px 0",
                    }}
                  >
                    POSITIONING
                  </p>
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.3px",
                      margin: 0,
                      color:
                        positioning === "Excellent Positioning"
                          ? "#26a65d"
                          : positioning === "Average Positioning"
                            ? "#ED9E0E"
                            : "#f23645",
                    }}
                  >
                    {positioning.replace(" Positioning", "").toUpperCase()}
                  </p>
                </div>
              )}

              {!editMode && sequence && (
                <div
                  id="setSequence"
                  onClick={() => editMode && handleEdit("setSequence")}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    backgroundColor:
                      sequence === "Excellent Sequence"
                        ? theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.15)"
                          : "rgba(38, 166, 93, 0.1)"
                        : sequence === "Average Sequence"
                          ? theme[0] === "dark"
                            ? "rgba(237, 158, 14, 0.15)"
                            : "rgba(237, 158, 14, 0.1)"
                          : theme[0] === "dark"
                            ? "rgba(242, 54, 69, 0.15)"
                            : "rgba(242, 54, 69, 0.1)",
                    border: `2px solid ${
                      sequence === "Excellent Sequence"
                        ? "#26a65d"
                        : sequence === "Average Sequence"
                          ? "#ED9E0E"
                          : "#f23645"
                    }`,
                    transition: "all 0.3s ease",
                    minWidth: width < 768 ? "100px" : "120px",
                    maxWidth: width < 768 ? "140px" : "160px",
                    textAlign: "center",
                    outline: "none",
                    WebkitTapHighlightColor: "transparent",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <p
                    style={{
                      fontSize: "8px",
                      fontWeight: "600",
                      letterSpacing: "0.8px",
                      color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                      margin: "0 0 2px 0",
                    }}
                  >
                    SEQUENCE
                  </p>
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.3px",
                      margin: 0,
                      color:
                        sequence === "Excellent Sequence"
                          ? "#26a65d"
                          : sequence === "Average Sequence"
                            ? "#ED9E0E"
                            : "#f23645",
                    }}
                  >
                    {sequence.replace(" Sequence", "").toUpperCase()}
                  </p>
                </div>
              )}

              {!editMode && grade && (
                <div
                  id="setGrade"
                  onClick={() => editMode && handleEdit("setGrade")}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    backgroundColor:
                      grade === "Low Risk"
                        ? theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.15)"
                          : "rgba(38, 166, 93, 0.1)"
                        : grade === "High Risk"
                          ? theme[0] === "dark"
                            ? "rgba(255, 165, 0, 0.15)"
                            : "rgba(255, 165, 0, 0.1)"
                        : grade === "Invalid"
                          ? theme[0] === "dark"
                            ? "rgba(242, 54, 69, 0.15)"
                            : "rgba(242, 54, 69, 0.1)"
                          : theme[0] === "dark"
                            ? "rgba(242, 54, 69, 0.15)"
                            : "rgba(242, 54, 69, 0.1)",
                    border: `2px solid ${
                      grade === "Low Risk"
                        ? "#26a65d"
                        : grade === "High Risk"
                          ? "#FFA500"
                          : grade === "Invalid"
                            ? "#f23645"
                            : "#f23645"
                    }`,
                    transition: "all 0.3s ease",
                    minWidth: width < 768 ? "100px" : "120px",
                    maxWidth: width < 768 ? "140px" : "160px",
                    textAlign: "center",
                    outline: "none",
                    WebkitTapHighlightColor: "transparent",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <p
                    style={{
                      fontSize: "8px",
                      fontWeight: "600",
                      letterSpacing: "0.8px",
                      color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                      margin: "0 0 2px 0",
                    }}
                  >
                    RISK GRADE
                  </p>
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.3px",
                      margin: 0,
                      color:
                        grade === "Low Risk"
                          ? "#26a65d"
                          : grade === "High Risk"
                            ? "#FFA500"
                            : grade === "Invalid"
                              ? "#f23645"
                              : "#f23645",
                    }}
                  >
                    {grade.replace(" Risk", "").toUpperCase()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Grid>

        {(editMode ||
          !pair ||
          !direction ||
          changeDate ||
          !positioning ||
          !sequence ||
          !grade ||
          !forecastType) && (
          <Grid item xs={12}>
            <div
              style={{
                display: width <= 768 ? "block" : "grid",
                gridTemplateColumns:
                  width <= 768 ? "1fr" : "repeat(auto-fit, minmax(350px, 1fr))",
                gap: width <= 768 ? "0" : "20px",
                maxWidth: "95%",
                margin: "1em auto",
              }}
            >
              {editMode || !pair ? (
                <div
                  style={{ margin: width <= 768 ? "1em auto 0.5em auto" : "0" }}
                >
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      SELECT INSTRUMENT
                    </p>
                    <InstrumentSelect
                      value={pair}
                      onChange={(event) => setPair(event.target.value)}
                      size="small"
                      fullWidth
                    />
                  </div>
                </div>
              ) : null}
              {editMode || !direction ? (
                <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      SELECT DIRECTION
                    </p>
                    <CustomSelect
                      size="small"
                      placeholder="Direction"
                      value={direction}
                      onChange={(e) => setDirection(e.target.value)}
                      menuItemArray={JSON.stringify([
                        { value: "Short", label: "Short" },
                        { value: "Long", label: "Long" },
                        { value: "Netural", label: "Netural" },
                      ])}
                    />
                  </div>
                </div>
              ) : null}

              {changeDate ? (
                <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      SELECT DATE
                    </p>
                    <CustomDatePicker
                      size="small"
                      label="Event Date"
                      value={date}
                      onChange={(newValue) => {
                        setChangeDate(false);
                        setDate(newValue.$d);
                      }}
                    />
                  </div>
                </div>
              ) : null}

              {editMode || !positioning ? (
                <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      POSITIONING
                    </p>
                    <CustomSelect
                      size="small"
                      placeholder="Positioning"
                      value={positioning}
                      onChange={(e) => setPositioning(e.target.value)}
                      menuItemArray={JSON.stringify([
                        { value: "Poor Positioning", label: "Poor" },
                        { value: "Average Positioning", label: "Average" },
                        { value: "Excellent Positioning", label: "Excellent" },
                      ])}
                    />
                  </div>
                </div>
              ) : null}

              {editMode || !sequence ? (
                <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      SEQUENCE
                    </p>
                    <CustomSelect
                      size="small"
                      placeholder="Sequence"
                      value={sequence}
                      onChange={(e) => setSequence(e.target.value)}
                      menuItemArray={JSON.stringify([
                        { value: "Poor Sequence", label: "Poor" },
                        { value: "Average Sequence", label: "Average" },
                        { value: "Excellent Sequence", label: "Excellent" },
                      ])}
                    />
                  </div>
                </div>
              ) : null}

              {editMode || !grade ? (
                <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      RISK GRADE
                    </p>
                    <CustomSelect
                      size="small"
                      placeholder="Risk Grade"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      menuItemArray={JSON.stringify([
                        { value: "Low Risk", label: "Low Risk" },
                        { value: "High Risk", label: "High Risk" },
                        { value: "Invalid", label: "Invalid" },
                      ])}
                    />
                  </div>
                </div>
              ) : null}
              {editMode || !forecastType ? (
                <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      FORECAST TYPE
                    </p>
                    <CustomSelect
                      size="small"
                      placeholder="Forecast Type"
                      value={forecastType}
                      onChange={(e) => setForecastType(e.target.value)}
                      menuItemArray={JSON.stringify([
                        { value: "Main", label: "Main" },
                        { value: "Additional", label: "Additional" },
                        { value: "Potential", label: "Potential" },
                      ])}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </Grid>
        )}

        <Grid container spacing={0} sx={{ maxWidth: "95%", margin: "0 auto" }}>
          {sections.map((section, index) => (
            <Grid
              key={`section-${index}`}
              item
              xs={12}
              md={6}
              sx={{ padding: { xs: "0", md: "8px" } }}
            >
              {/* Before Section */}
              <div
                style={{
                  width: "100%",
                  minHeight: "60px",
                  margin: index > 0 && width <= 768
                    ? "2em auto 0.1em auto"
                    : "0 auto",
                }}
              >
                {section.timeframeEdit || !section.timeframeName ? (
                  <div
                    style={{
                      maxWidth: "400px",
                      margin: "0 auto 1em auto",
                      padding: width <= 768 ? "16px" : "20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 12px 0",
                        textAlign: "center",
                      }}
                    >
                      SELECT TIMEFRAME
                    </p>
                    <CustomSelect
                      size="small"
                      placeholder="Timeframe"
                      value={section.timeframeName}
                      onChange={(e) => {
                        updateSection(index, "timeframeName", e.target.value);
                        updateSection(index, "timeframeEdit", false);
                      }}
                      menuItemArray={JSON.stringify([
                        { label: "5m", value: "5m" },
                        { label: "15m", value: "15m" },
                        { label: "1h", value: "1h" },
                        { label: "4h", value: "4h" },
                        { label: "Daily", value: "Daily" },
                        { label: "Weekly", value: "Weekly" },
                        { label: "Monthly", value: "Monthly" },
                      ])}
                    />
                  </div>
                ) : (
                  <div
                    onClick={() =>
                      updateSection(index, "timeframeEdit", true)
                    }
                    style={{
                      cursor: "pointer",
                      padding: "10px 16px",
                      margin: "0.5em auto 0.3em auto",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `2px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                      textAlign: "center",
                      maxWidth: "160px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.02)";
                      e.currentTarget.style.borderColor =
                        theme[0] === "dark" ? "#9ca3af" : "#6b7280";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.borderColor =
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
                    }}
                  >
                    <p
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                        margin: 0,
                      }}
                    >
                      {section.timeframeName} Before
                    </p>
                  </div>
                )}
              </div>
              {section.screenshotLink ? (
                section.screenshotLink.startsWith(
                  "https://www.tradingview.com",
                ) ? (
                  <div
                    style={{ position: "relative" }}
                    onMouseEnter={() => handleImageMouseEnter(index)}
                    onMouseLeave={() => handleImageMouseLeave(index)}
                  >
                    <img
                      style={imgStyle}
                      src={section.screenshotLink}
                      alt={`${section.timeframeName} Screenshot`}
                      height="auto"
                      onClick={() => openImageViewer(index)}
                    />
                    {showDeleteIcon === index && (
                      <Tooltip title="Delete screenshot">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteScreenshot(index, "screenshotLink");
                          }}
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: "50%",
                            padding: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(242, 54, 69, 0.9)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(0, 0, 0, 0.7)";
                          }}
                        >
                          <DeleteOutlinedIcon
                            style={{
                              color: "#fff",
                              fontSize: "20px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#f23645",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      updateSection(index, "screenshotLink", "")
                    }
                  >
                    Invalid Link
                  </p>
                )
              ) : !editMode ? (
                <div
                  style={{
                    minHeight: "60px",
                    margin: "0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "15px",
                      color: "#f23645",
                      margin: 0,
                    }}
                  >
                    No Screenshot Detected
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    minHeight: "60px",
                    margin: "0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <CustomMultiTextField
                    labelColor="#f23645"
                    size="small"
                    rows={calculateRows(section.screenshotLink)}
                    variant="standard"
                    value={section.screenshotLink}
                    label="No Screenshot Detected"
                    onChange={(e) => {
                      updateSection(index, "screenshotLink", e.target.value);
                    }}
                    fontSize="16px"
                    disableUnderline={true}
                  />
                </div>
              )}

              <div
                style={{
                  margin: "1em auto 0 auto",
                  padding: "16px",
                  borderRadius: "12px",
                  backgroundColor:
                    theme[0] === "dark"
                      ? "rgba(45, 46, 51, 0.4)"
                      : "rgba(249, 250, 251, 0.8)",
                  border: `1px solid ${
                    theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                  }`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                      margin: 0,
                    }}
                  >
                    {section.timeframeName
                      ? `${section.timeframeName} Description (Before)`
                      : "Description (Before)"}
                  </p>
                  <VoiceToText
                    onTranscript={(val) =>
                      updateSection(index, "description", val)
                    }
                    currentValue={section.description}
                  />
                </div>
                <CustomMultiTextField
                  size="small"
                  rows={calculateRows(section.description)}
                  variant="standard"
                  value={section.description}
                  onChange={(e) => {
                    updateSection(index, "description", e.target.value);
                  }}
                  fontSize="16px"
                  disableUnderline={true}
                />
              </div>

              {/* DXY Screenshot Section - Only for USD-related pairs */}
              {isUSDRelatedPair(pair) && section.timeframeName && (
                <>
                  <div
                    style={{
                      cursor: "default",
                      padding: "10px 16px",
                      margin: "1.5em auto 0.3em auto",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `2px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                      textAlign: "center",
                      maxWidth: "160px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#ED9E0E",
                        margin: 0,
                      }}
                    >
                      DXY {section.timeframeName}
                    </p>
                  </div>
                  {section.dxyScreenshotLink ? (
                    section.dxyScreenshotLink.startsWith(
                      "https://www.tradingview.com"
                    ) ? (
                      <div
                        style={{ position: "relative" }}
                        onMouseEnter={() => handleImageMouseEnter(index + 1000)}
                        onMouseLeave={() => handleImageMouseLeave(index + 1000)}
                      >
                        <img
                          style={imgStyle}
                          src={section.dxyScreenshotLink}
                          alt={`DXY ${section.timeframeName} Screenshot`}
                          height="auto"
                          onClick={() => openImageViewer(index + 1000)}
                        />
                        {showDeleteIcon === index + 1000 && editMode && (
                          <Tooltip title="Delete DXY screenshot">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteScreenshot(index, "dxyScreenshotLink");
                              }}
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                                borderRadius: "50%",
                                padding: "8px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(242, 54, 69, 0.9)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(0, 0, 0, 0.7)";
                              }}
                            >
                              <DeleteOutlinedIcon
                                style={{
                                  color: "#fff",
                                  fontSize: "20px",
                                }}
                              />
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    ) : (
                      editMode && (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#f23645",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            updateSection(index, "dxyScreenshotLink", "")
                          }
                        >
                          Invalid DXY Link
                        </p>
                      )
                    )
                  ) : !editMode ? (
                    <div
                      style={{
                        minHeight: "60px",
                        margin: "0 auto",
                        padding: "16px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "15px",
                          color: "#f23645",
                          margin: 0,
                        }}
                      >
                        No Screenshot Detected
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        minHeight: "60px",
                        margin: "0 auto",
                        padding: "16px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <CustomMultiTextField
                        labelColor="#f23645"
                        size="small"
                        rows={calculateRows(section.dxyScreenshotLink)}
                        variant="standard"
                        value={section.dxyScreenshotLink}
                        label="No Screenshot Detected"
                        onChange={(e) => {
                          updateSection(index, "dxyScreenshotLink", e.target.value);
                        }}
                        fontSize="16px"
                        disableUnderline={true}
                      />
                    </div>
                  )}
                </>
              )}

              {/* After Section - directly below Before */}
              {!showAfterSections[index] && !section.afterScreenshotLink && !section.afterDescription ? (
                <div
                  style={{
                    width: "100%",
                    marginTop: "2em",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <div
                    onClick={() => toggleAfterSection(index)}
                    style={{
                      cursor: "pointer",
                      padding: "10px 20px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(156, 163, 175, 0.1)"
                          : "rgba(107, 114, 128, 0.08)",
                      border: `2px dashed ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                      textAlign: "center",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        theme[0] === "dark"
                          ? "rgba(237, 158, 14, 0.15)"
                          : "rgba(237, 158, 14, 0.1)";
                      e.currentTarget.style.borderColor = "#ED9E0E";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        theme[0] === "dark"
                          ? "rgba(156, 163, 175, 0.1)"
                          : "rgba(107, 114, 128, 0.08)";
                      e.currentTarget.style.borderColor =
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: 0,
                      }}
                    >
                      + Add {section.timeframeName} After
                    </p>
                  </div>
                </div>
              ) : null}
              
              {showAfterSections[index] || section.afterScreenshotLink || section.afterDescription ? (
                <>
                  <div
                    style={{
                      width: "100%",
                      minHeight: "60px",
                      marginTop: "2em",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          cursor: "default",
                          padding: "10px 16px",
                          margin: "0.5em auto 0.3em auto",
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(156, 163, 175, 0.1)"
                              : "rgba(107, 114, 128, 0.08)",
                          border: `1px solid ${
                            theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                          }`,
                          textAlign: "center",
                          maxWidth: "160px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                            margin: 0,
                          }}
                        >
                          {section.timeframeName} After
                        </p>
                      </div>
                      {!section.afterScreenshotLink && !section.afterDescription && (
                        <div
                          onClick={() => toggleAfterSection(index)}
                          style={{
                            cursor: "pointer",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            backgroundColor:
                              theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                            border: `1px solid #f23645`,
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.25)"
                                : "rgba(242, 54, 69, 0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)";
                          }}
                        >
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#f23645",
                              margin: 0,
                            }}
                          >
                            Hide
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

              {section.afterScreenshotLink ? (
                section.afterScreenshotLink.startsWith(
                  "https://www.tradingview.com",
                ) ? (
                  <div
                    style={{ position: "relative" }}
                    onMouseEnter={() => handleImageMouseEnter(sections.length + index)}
                    onMouseLeave={() => handleImageMouseLeave(sections.length + index)}
                  >
                    <img
                      style={imgStyle}
                      src={section.afterScreenshotLink}
                      alt={`${section.timeframeName} Screenshot After`}
                      height="auto"
                      onClick={() => openImageViewer(sections.length + index)}
                    />
                    {showDeleteIcon === sections.length + index && (
                      <Tooltip title="Delete screenshot">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteScreenshot(index, "afterScreenshotLink");
                          }}
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: "50%",
                            padding: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(242, 54, 69, 0.9)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(0, 0, 0, 0.7)";
                          }}
                        >
                          <DeleteOutlinedIcon
                            style={{
                              color: "#fff",
                              fontSize: "20px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#f23645",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      updateSection(index, "afterScreenshotLink", "")
                    }
                  >
                    Invalid Link
                  </p>
                )
              ) : !editMode ? (
                <div
                  style={{
                    margin: "0 auto",
                    minHeight: "60px",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "15px",
                      color: "#f23645",
                      margin: 0,
                    }}
                  >
                    No Screenshot Detected
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    margin: "0 auto",
                    minHeight: "60px",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <CustomMultiTextField
                    labelColor="#f23645"
                    size="small"
                    rows={calculateRows(section.afterScreenshotLink)}
                    variant="standard"
                    value={section.afterScreenshotLink}
                    label="No Screenshot Detected"
                    onChange={(e) => {
                      updateSection(index, "afterScreenshotLink", e.target.value);
                    }}
                    fontSize="16px"
                    disableUnderline={true}
                  />
                </div>
              )}

              <div
                style={{
                  margin: "1em auto 0 auto",
                  padding: "16px",
                  borderRadius: "12px",
                  backgroundColor:
                    theme[0] === "dark"
                      ? "rgba(45, 46, 51, 0.4)"
                      : "rgba(249, 250, 251, 0.8)",
                  border: `1px solid ${
                    theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                  }`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                      margin: 0,
                    }}
                  >
                    {section.timeframeName} Description (After)
                  </p>
                  <VoiceToText
                    onTranscript={(val) =>
                      updateSection(index, "afterDescription", val)
                    }
                    currentValue={section.afterDescription}
                  />
                </div>
                <CustomMultiTextField
                  size="small"
                  rows={calculateRows(section.afterDescription)}
                  variant="standard"
                  value={section.afterDescription}
                  label={section.afterDescription ? "" : ""}
                  onChange={(e) => {
                    updateSection(index, "afterDescription", e.target.value);
                  }}
                  fontSize="16px"
                  disableUnderline={true}
                />
              </div>

              {/* DXY After Screenshot Section - Only for USD-related pairs */}
              {isUSDRelatedPair(pair) && section.timeframeName && (
                <>
                  <div
                    style={{
                      cursor: "default",
                      padding: "10px 16px",
                      margin: "1.5em auto 0.3em auto",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `2px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                      textAlign: "center",
                      maxWidth: "160px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#ED9E0E",
                        margin: 0,
                      }}
                    >
                      DXY {section.timeframeName} After
                    </p>
                  </div>
                  {section.dxyAfterScreenshotLink ? (
                    section.dxyAfterScreenshotLink.startsWith(
                      "https://www.tradingview.com"
                    ) ? (
                      <div
                        style={{ position: "relative" }}
                        onMouseEnter={() => handleImageMouseEnter(sections.length * 2 + index + 1000)}
                        onMouseLeave={() => handleImageMouseLeave(sections.length * 2 + index + 1000)}
                      >
                        <img
                          style={imgStyle}
                          src={section.dxyAfterScreenshotLink}
                          alt={`DXY ${section.timeframeName} Screenshot After`}
                          height="auto"
                          onClick={() => openImageViewer(sections.length * 2 + index + 1000)}
                        />
                        {showDeleteIcon === sections.length * 2 + index + 1000 && editMode && (
                          <Tooltip title="Delete DXY after screenshot">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteScreenshot(index, "dxyAfterScreenshotLink");
                              }}
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                                borderRadius: "50%",
                                padding: "8px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(242, 54, 69, 0.9)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(0, 0, 0, 0.7)";
                              }}
                            >
                              <DeleteOutlinedIcon
                                style={{
                                  color: "#fff",
                                  fontSize: "20px",
                                }}
                              />
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    ) : (
                      editMode && (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#f23645",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            updateSection(index, "dxyAfterScreenshotLink", "")
                          }
                        >
                          Invalid DXY Link
                        </p>
                      )
                    )
                  ) : !editMode ? (
                    <div
                      style={{
                        margin: "0 auto",
                        minHeight: "60px",
                        padding: "16px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "15px",
                          color: "#f23645",
                          margin: 0,
                        }}
                      >
                        No Screenshot Detected
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        margin: "0 auto",
                        minHeight: "60px",
                        padding: "16px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <CustomMultiTextField
                        labelColor="#f23645"
                        size="small"
                        rows={calculateRows(section.dxyAfterScreenshotLink)}
                        variant="standard"
                        value={section.dxyAfterScreenshotLink}
                        label="No Screenshot Detected"
                        onChange={(e) => {
                          updateSection(index, "dxyAfterScreenshotLink", e.target.value);
                        }}
                        fontSize="16px"
                        disableUnderline={true}
                      />
                    </div>
                  )}
                </>
              )}
                </>
              ) : null}
            </Grid>
          ))}
        </Grid>

        <div style={{ maxWidth: "300px", margin: "2em auto 1em auto" }}>
          {!tradeId && showTradeInfo && (
            <div
              style={{
                marginBottom: "1em",
                padding: "16px",
                borderRadius: "16px",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(45, 46, 51, 0.6)"
                    : "rgba(249, 250, 251, 0.9)",
                border: `2px solid ${
                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                }`,
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                Trades are linked automatically when you create a trade from
                this forecast.
              </p>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "row", gap: "12px", justifyContent: "center" }}>
            {liveTradeIds.length > 0 && (
              <div
                onClick={() => handleViewTrades("live", liveTradeIds)}
                style={{
                  cursor: "pointer",
                  padding: "12px 20px",
                  borderRadius: "20px",
                  backgroundColor: theme[0] === "dark"
                    ? "rgba(38, 166, 93, 0.12)"
                    : "rgba(38, 166, 93, 0.08)",
                  border: "2px solid #26a65d",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme[0] === "dark"
                    ? "rgba(38, 166, 93, 0.2)"
                    : "rgba(38, 166, 93, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme[0] === "dark"
                    ? "rgba(38, 166, 93, 0.12)"
                    : "rgba(38, 166, 93, 0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <span
                  style={{
                    color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                    fontSize: "12px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    marginRight: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  LIVE TRADES
                </span>
                <span
                  style={{
                    color: "#26a65d",
                    fontSize: "16px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {liveTradeIds.length > 1
                    ? `View Trades (${liveTradeIds.length})`
                    : "View Trade"}
                </span>
              </div>
            )}
            {backtestTradeIds.length > 0 && (
              <div
                onClick={() => handleViewTrades("backtest", backtestTradeIds)}
                style={{
                  cursor: "pointer",
                  padding: "12px 20px",
                  borderRadius: "20px",
                  backgroundColor: theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.12)"
                    : "rgba(237, 158, 14, 0.08)",
                  border: "2px solid #ED9E0E",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.2)"
                    : "rgba(237, 158, 14, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.12)"
                    : "rgba(237, 158, 14, 0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <span
                  style={{
                    color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                    fontSize: "12px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    marginRight: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {backtestTitle || "BACKTEST TRADES"}
                </span>
                <span
                  style={{
                    color: "#ED9E0E",
                    fontSize: "16px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {backtestTradeIds.length > 1
                    ? `View Trades (${backtestTradeIds.length})`
                    : "View Trade"}
                </span>
              </div>
            )}
            {liveTradeIds.length === 0 && backtestTradeIds.length === 0 && (
              <div
                onClick={() => setShowTradeInfo(!showTradeInfo)}
                style={{
                  cursor: "pointer",
                  padding: "12px 20px",
                  borderRadius: "20px",
                  backgroundColor: theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.12)"
                    : "rgba(237, 158, 14, 0.08)",
                  border: "2px solid #ED9E0E",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.2)"
                    : "rgba(237, 158, 14, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.12)"
                    : "rgba(237, 158, 14, 0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <span
                  style={{
                    color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                    fontSize: "12px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  LINKED TRADE
                </span>
                <span
                  style={{
                    color: "#ED9E0E",
                    fontSize: "16px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {showTradeInfo ? "Close" : "No Linked Trade"}
                </span>
              </div>
            )}
          </div>
        </div>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "2em 0 1em 0",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="Click to Delete">
            <div
              onClick={() => {
                setShowDeleteConfirm(true);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "24px",
                cursor: "pointer",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(242, 54, 69, 0.12)"
                    : "rgba(242, 54, 69, 0.08)",
                border: "2px solid #f23645",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme[0] === "dark"
                    ? "rgba(242, 54, 69, 0.2)"
                    : "rgba(242, 54, 69, 0.15)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme[0] === "dark"
                    ? "rgba(242, 54, 69, 0.12)"
                    : "rgba(242, 54, 69, 0.08)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <DeleteOutlinedIcon
                sx={{
                  color: "#f23645",
                  fontSize: "24px",
                }}
              />
              <span
                style={{
                  color: "#f23645",
                  fontWeight: "600",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                  display: width < 768 ? "none" : "inline",
                }}
              >
                DELETE
              </span>
            </div>
          </Tooltip>

          <Tooltip title={editMode ? "Exit Edit Mode" : "Enter Edit Mode"}>
            <div
              onClick={() => {
                const newEditMode = !editMode;
                setEditMode(newEditMode);
                if (newEditMode) {
                  // Show all edit fields
                  setSections((prev) =>
                    prev.map((s) => ({ ...s, timeframeEdit: true })),
                  );
                } else {
                  // Hide edit fields when exiting edit mode
                  setChangeDate(false);
                  setSections((prev) =>
                    prev.map((s) => ({ ...s, timeframeEdit: false })),
                  );
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "24px",
                cursor: "pointer",
                backgroundColor: editMode
                  ? theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.12)"
                    : "rgba(237, 158, 14, 0.08)"
                  : theme[0] === "dark"
                    ? "rgba(156, 163, 175, 0.12)"
                    : "rgba(107, 114, 128, 0.08)",
                border: `2px solid ${
                  editMode
                    ? "#ED9E0E"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280"
                }`,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = editMode
                  ? theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.2)"
                    : "rgba(237, 158, 14, 0.15)"
                  : theme[0] === "dark"
                    ? "rgba(156, 163, 175, 0.2)"
                    : "rgba(107, 114, 128, 0.15)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = editMode
                  ? theme[0] === "dark"
                    ? "rgba(237, 158, 14, 0.12)"
                    : "rgba(237, 158, 14, 0.08)"
                  : theme[0] === "dark"
                    ? "rgba(156, 163, 175, 0.12)"
                    : "rgba(107, 114, 128, 0.08)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {editMode ? (
                <EditOffIcon
                  sx={{
                    color: "#ED9E0E",
                    fontSize: "24px",
                  }}
                />
              ) : (
                <EditIcon
                  sx={{
                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                    fontSize: "24px",
                  }}
                />
              )}
              <span
                style={{
                  color: editMode
                    ? "#ED9E0E"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                  display: width < 768 ? "none" : "inline",
                }}
              >
                {editMode ? "EDITING" : "EDIT"}
              </span>
            </div>
          </Tooltip>

          <Tooltip title={isSharing ? "Generating..." : "Share Forecast"}>
            <div
              onClick={handleShare}
              onMouseEnter={() => setIsHoveringShare(true)}
              onMouseLeave={(e) => {
                setIsHoveringShare(false);
                if (!isSharing) {
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.12)"
                      : "rgba(107, 114, 128, 0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "24px",
                cursor: isSharing ? "not-allowed" : "pointer",
                backgroundColor:
                  isHoveringShare || isSharing
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)"
                    : theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.12)"
                      : "rgba(107, 114, 128, 0.08)",
                border: `2px solid ${
                  isHoveringShare || isSharing
                    ? "#3B82F6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280"
                }`,
                color:
                  isHoveringShare || isSharing
                    ? "#3B82F6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                transition: "all 0.3s ease",
                opacity: isSharing ? 0.6 : 1,
              }}
            >
              <ShareIcon
                sx={{
                  color:
                    isHoveringShare || isSharing
                      ? "#3B82F6"
                      : theme[0] === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                  fontSize: "24px",
                }}
              />
              <span
                style={{
                  color:
                    isHoveringShare || isSharing
                      ? "#3B82F6"
                      : theme[0] === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                  display: width < 768 ? "none" : "inline",
                }}
              >
                {isSharing ? "GENERATING..." : "SHARE"}
              </span>
            </div>
          </Tooltip>

          <Tooltip title="Close">
            <div
              onClick={() => goBack()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "24px",
                cursor: "pointer",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(156, 163, 175, 0.12)"
                    : "rgba(107, 114, 128, 0.08)",
                border: `2px solid ${
                  theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                }`,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme[0] === "dark"
                    ? "rgba(156, 163, 175, 0.2)"
                    : "rgba(107, 114, 128, 0.15)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme[0] === "dark"
                    ? "rgba(156, 163, 175, 0.12)"
                    : "rgba(107, 114, 128, 0.08)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <CloseIcon
                sx={{
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  fontSize: "24px",
                }}
              />
              <span
                style={{
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                  display: width < 768 ? "none" : "inline",
                }}
              >
                CLOSE
              </span>
            </div>
          </Tooltip>

          <Tooltip title={formSubmitted ? "Saving..." : "Save Changes"}>
            <div
              onClick={() => {
                if (!formSubmitted) handleSave();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "24px",
                cursor: formSubmitted ? "not-allowed" : "pointer",
                backgroundColor:
                  theme[0] === "dark"
                    ? formSubmitted
                      ? "rgba(156, 163, 175, 0.12)"
                      : "rgba(38, 166, 93, 0.12)"
                    : formSubmitted
                      ? "rgba(107, 114, 128, 0.08)"
                      : "rgba(38, 166, 93, 0.08)",
                border: `2px solid ${formSubmitted ? "#9ca3af" : "#26a65d"}`,
                transition: "all 0.3s ease",
                opacity: formSubmitted ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!formSubmitted) {
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(38, 166, 93, 0.2)"
                      : "rgba(38, 166, 93, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!formSubmitted) {
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(38, 166, 93, 0.12)"
                      : "rgba(38, 166, 93, 0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
            >
              <SaveIcon
                sx={{
                  color: formSubmitted ? "#9ca3af" : "#26a65d",
                  fontSize: "24px",
                }}
              />
              <span
                style={{
                  color: formSubmitted ? "#9ca3af" : "#26a65d",
                  fontWeight: "600",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                  display: width < 768 ? "none" : "inline",
                }}
              >
                SAVE
              </span>
            </div>
          </Tooltip>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteForecast(goBack)}
        title="Delete Forecast"
        message="Are you sure you want to delete this forecast?"
      />

      <ConfirmDialog
        open={showScreenshotDeleteConfirm}
        onClose={() => setShowScreenshotDeleteConfirm(false)}
        onConfirm={confirmDeleteScreenshot}
        title="Delete Screenshot"
        message="Are you sure you want to delete this screenshot?"
      />
    </Box>
  );
};

export default SingleForecasts;
